import {Button, Checkbox, RemoveTab, TextInput} from "omoplay";
import {useEffect, useState} from "react";
import InputGroup from "../InputGroup";
import {getSurvey, postSurveyQuestionsSave, postSurveyUsedSave} from "../../../api/project/project.api";
import SuspensePopup from "../../SuspensePopup";
import {ChevronDown, ChevronUp, X} from "lucide-react";
import {
    changeQuestionType,
    DEFAULT_QUESTION_TYPE,
    manualNumberPrefix,
    moveQuestion,
    newQuestion,
    QUESTION_TYPES,
    questionNumberLabel,
    splitPastedLines,
    stripKeys,
    withKeys
} from "../../../libs/surveyQuestions";
import SurveyPreview from "./SurveyPreview";


/*
  설문 편집 화면 정리(2026-10-12 사용자 요청)
  - 문항마다 박스 + 순번, 머리줄에 유형 풀다운 · 필수 · 순서 이동 · 문항 삭제(휴지통)
  - 새 문항 기본값 = 객관식 · 필수(2026-10-23, 종전 주관식 · 비필수). 유형에 '5점 척도' — 선택지 5개가 고정으로 붙고 고칠 수 없다
  - 선택지 입력칸에 여러 줄을 붙여넣으면 줄마다 선택지로 쪼개 넣는다(AI 가 만든 설문의 선택지를 통째로 복사해 오는 경우, libs/surveyQuestions.js)
  - 질문 입력은 박스 전체 폭, 객관식 선택지는 들여쓰고 폭을 줄여 문항보다 아래 단계로 보이게
  - 선택지 삭제는 X(문항 삭제의 붉은 휴지통과 구분 — 휴지통이 너무 많아 실수 위험)
  - '질문 추가하기' 는 보조 버튼(외곽선)으로 — 주 버튼 색이 과하게 시선을 끌었다
  - 프로젝트 상세 설문 섹션에 '설문 미리보기'(실제 고객 화면을 iframe 으로, SurveyPreview)
  🔴 응답은 문항 **제목**으로 저장된다 — 순서·유형 표시를 바꿔도 키는 그대로이고, 제목을 바꾸면 과거 응답과 끊긴다.
  순번 '1.' 은 자동으로 붙는다(질문 입력 왼쪽, 고객 화면·분석 탭도 같은 표기) — 제목에는 번호 없이 내용만 쓴다(2026-10-12, 표기는 2026-10-23 에 '(1)' → '1.').
*/
const ProjectDetailPrecondition = ({projectId}) => {
    const [useChecked, setUseChecked] = useState(false)
    const [showSurvey, setShowSurvey] = useState(false)
    const [showPreview, setShowPreview] = useState(false)
    const [title, setTitle] = useState("설문조사")
    const [description, setDescription] = useState("설문조사를 진행하시면 혜택을 받으실 수 있습니다.")
    const [questions, setQuestions] = useState([])

    const onSubmit = () => {
        postSurveyUsedSave(projectId, useChecked).then(() => {
            alert("저장 되었습니다.")
        })
    }

    useEffect(() => {
        getSurvey(projectId).then(({data}) => {
            if (data) {
                setUseChecked(data.used)
                setTitle(data.title)
                setDescription(data.description)
                setQuestions(withKeys(data.questions))
            }
        })
    }, [projectId]);

    const updateQuestion = (index, patch) =>
        setQuestions(questions.map((q, i) => i === index ? {...q, ...patch} : q))

    return (
        <>
            <InputGroup label={
                <div className="flex-align-center justify-between">
                    <div className="text-[16px]">이벤트 참여 선행 조건</div>
                    <div className="flex-align-center gap-4">
                        <Button variants="dark" value="적용하기" onClick={() => onSubmit()} />
                    </div>
                </div>
            } description="이벤트에 참여하기 전 조건을 설정합니다.">
                <div className="flex-align-center gap-4">
                    <Checkbox checked={useChecked} onChecked={setUseChecked}
                              item={{label: "설문조사를 진행합니다.", value: "used"}} />
                    <Button variants="primary" value="설문조사 폼 작성" onClick={() => setShowSurvey(true)} />
                    <Button variants="outlined" value="설문 미리보기" onClick={() => setShowPreview(true)} />
                </div>
            </InputGroup>
            <SuspensePopup
                title="설문조사 추가하기"
                visible={showSurvey}
                style={{maxHeight: "1200px"}}
                confirmDisabled={questions.length === 0}
                confirmText="저장"
                onConfirm={() => {
                    // 화면 전용 키(_k)는 떼고 저장한다 — 설문 JSON 형식은 그대로(libs/surveyQuestions.js)
                    postSurveyQuestionsSave(projectId, title, description, stripKeys(questions)).then(() => {
                        setShowSurvey(false)
                    })
                }}
                onClose={() => setShowSurvey(false)}
            >
                <div>
                    <div>메인 제목</div>
                    <TextInput value={title}
                               style={{ fontSize: "24px", fontWeight: "500" }}
                               onChange={(e) => {
                                   setTitle(e.target.value)
                               }}
                    />
                </div>
                <div className="mb-8">
                    <div>메인 설명</div>
                    <TextInput value={description}
                               onChange={(e) => {
                                   setDescription(e.target.value)
                               }}
                    />
                </div>
                <div className="border-b-2 mb-8"></div>
                <div className="flex-col gap-4">
                    {
                        questions.map((question, index) => {
                            const isMultipleChoice = question.type === "MULTIPLE_CHOICE"
                            const isScale = question.type === "SCALE_5"
                            const manualNumber = manualNumberPrefix(question.title)
                            return (
                                <div key={question._k} className="flex-col gap-3 rounded-xl border border-gray-200 bg-white p-4">
                                    {/* 머리줄: 유형 · 필수 · 순서 · 삭제 */}
                                    <div className="flex-align-center gap-3">
                                        <select
                                            value={question.type ?? DEFAULT_QUESTION_TYPE}
                                            onChange={(e) => updateQuestion(index, changeQuestionType(question, e.target.value))}
                                            className="h-9 rounded-lg border border-gray-300 bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        >
                                            {QUESTION_TYPES.map(type => (
                                                <option key={type.value} value={type.value}>{type.label}</option>
                                            ))}
                                        </select>
                                        <Checkbox item={{label: "필수", value: "required"}}
                                                  checked={question.required}
                                                  onChecked={(checked) => updateQuestion(index, {required: checked})}
                                        />
                                        <div className="ml-auto flex-align-center gap-1">
                                            {/* 순서 바꾸기 — 응답은 제목으로 저장되므로 순서를 바꿔도 기존 응답과 연결이 유지된다 */}
                                            <button type="button" title="위로" disabled={index === 0}
                                                    className="rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-900 disabled:opacity-30 disabled:cursor-not-allowed"
                                                    onClick={() => setQuestions(moveQuestion(questions, index, -1))}>
                                                <ChevronUp size={18} />
                                            </button>
                                            <button type="button" title="아래로" disabled={index === questions.length - 1}
                                                    className="rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-900 disabled:opacity-30 disabled:cursor-not-allowed"
                                                    onClick={() => setQuestions(moveQuestion(questions, index, +1))}>
                                                <ChevronDown size={18} />
                                            </button>
                                            {/* 문항 삭제 = 휴지통(선택지 삭제 X 와 구분) */}
                                            <RemoveTab onClick={() => setQuestions(questions.filter((_, i) => i !== index))} />
                                        </div>
                                    </div>
                                    {/* 질문 — 자동 순번 '1.' + 박스 나머지 폭 */}
                                    <div className="flex-align-center gap-3">
                                        <span className="shrink-0 text-[17px] font-semibold text-gray-900">
                                            {questionNumberLabel(index)}
                                        </span>
                                        <div className="flex-1">
                                            <TextInput value={question.title}
                                                       placeholder="질문 내용만 입력하세요 (번호는 자동으로 붙습니다)"
                                                       style={{ fontSize: "17px", fontWeight: "600" }}
                                                       onChange={(e) => updateQuestion(index, {title: e.target.value})}
                                            />
                                        </div>
                                    </div>
                                    {
                                        manualNumber && (
                                            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded px-2 py-1.5 leading-relaxed">
                                                제목 앞의 '{manualNumber}' 는 자동 순번과 겹칩니다. 새 설문이면 지우고 내용만 쓰세요.
                                                이미 응답이 있는 설문이면 제목을 바꾸는 순간 그 문항의 이전 응답과 연결이 끊기니(분석 탭에 '이전 문항' 으로 따로 나옴) 그대로 두는 것을 권합니다.
                                            </p>
                                        )
                                    }
                                    {/* 5점 척도 — 선택지는 고정(libs/surveyQuestions.js SCALE_5_VALUES), 읽기 전용 */}
                                    {
                                        isScale && (
                                            <div className="flex-col gap-2 pl-6">
                                                {
                                                    (question.values ?? []).map((value, valueIndex) => (
                                                        <div className="flex-align-center gap-2 max-w-[460px]" key={`${question._k}-${valueIndex}`}>
                                                            <span className="h-4 w-4 shrink-0 rounded-full border-2 border-gray-300" />
                                                            <div className="flex-1">
                                                                <TextInput value={value} readOnly={true} />
                                                            </div>
                                                        </div>
                                                    ))
                                                }
                                                <p className="pl-6 text-xs text-gray-500">5점 척도의 선택지는 고정이며 수정할 수 없습니다.</p>
                                            </div>
                                        )
                                    }
                                    {/* 객관식 선택지 — 들여쓰고 폭을 줄여 문항보다 아래 단계로 */}
                                    {
                                        isMultipleChoice && (
                                            <div className="flex-col gap-2 pl-6">
                                                {
                                                    question.values && question.values.map((value, valueIndex) => (
                                                        <div className="flex-align-center gap-2 max-w-[460px]" key={`${question._k}-${valueIndex}`}>
                                                            <span className="h-4 w-4 shrink-0 rounded-full border-2 border-gray-300" />
                                                            <div className="flex-1">
                                                                <TextInput value={value}
                                                                           placeholder={`선택지 ${valueIndex + 1}`}
                                                                           onChange={(e) => updateQuestion(index, {
                                                                               values: question.values.map((v, vi) => vi === valueIndex ? e.target.value : v)
                                                                           })}
                                                                           onPaste={(e) => {
                                                                               // 여러 줄이면 이 칸은 첫 줄로 바꾸고 나머지 줄은 바로 아래에 선택지로 끼운다. 한 줄이면 기본 붙여넣기.
                                                                               const lines = splitPastedLines(e.clipboardData?.getData("text"))
                                                                               if (lines.length < 2) return
                                                                               e.preventDefault()
                                                                               const values = [...question.values]
                                                                               values.splice(valueIndex, 1, ...lines)
                                                                               updateQuestion(index, {values})
                                                                           }}
                                                                />
                                                            </div>
                                                            <button type="button" title="선택지 삭제"
                                                                    className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                                                                    onClick={() => updateQuestion(index, {
                                                                        values: question.values.filter((_, vi) => vi !== valueIndex)
                                                                    })}>
                                                                <X size={16} />
                                                            </button>
                                                        </div>
                                                    ))
                                                }
                                                {
                                                    (question.etc === true) && (
                                                        <div className="flex-align-center gap-2 max-w-[460px]" key={`${question._k}-etc`}>
                                                            <span className="h-4 w-4 shrink-0 rounded-full border-2 border-gray-300" />
                                                            <div className="flex-1">
                                                                <TextInput value="기타(직접입력)" readOnly={true} />
                                                            </div>
                                                            <button type="button" title="'기타' 삭제"
                                                                    className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                                                                    onClick={() => updateQuestion(index, {etc: false})}>
                                                                <X size={16} />
                                                            </button>
                                                        </div>
                                                    )
                                                }
                                                <div className="flex-align-center gap-3 pl-6 text-sm">
                                                    <button type="button" className="text-gray-600 hover:text-gray-900"
                                                            onClick={() => updateQuestion(index, {values: [...question.values ?? [], ""]})}>
                                                        + 선택지 추가
                                                    </button>
                                                    {
                                                        !question.etc && (
                                                            <button type="button" className="text-gray-600 hover:text-gray-900"
                                                                    onClick={() => updateQuestion(index, {etc: true})}>
                                                                + '기타(직접입력)' 추가
                                                            </button>
                                                        )
                                                    }
                                                </div>
                                            </div>
                                        )
                                    }
                                </div>
                            )
                        })
                    }
                    <Button variants="outlined" value="+ 질문 추가하기" onClick={() => {
                        // 기본 = 객관식 · 필수(2026-10-23). 종전의 `text: "제목 없음"` 은 서버 모델에 없는 필드라 저장 때 버려지던 값이었다.
                        setQuestions([...questions, newQuestion()])
                    }} />
                </div>
            </SuspensePopup>
            <SuspensePopup
                title="설문 미리보기"
                visible={showPreview}
                style={{width: "460px"}}
                confirmText="닫기"
                onConfirm={() => setShowPreview(false)}
                onClose={() => setShowPreview(false)}
            >
                {showPreview && <SurveyPreview title={title} description={description} questions={questions} />}
            </SuspensePopup>
        </>
    )
}

export default ProjectDetailPrecondition
